import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MixedCopy from '@components/molecules/mixed-copy/mixedCopy';
import Headline from '@components/atoms/headline/headline';
import CompactLoginMask from '@components/molecules/compact-login-mask/compactLoginMask';
import Copy from '@components/atoms/copy/copy';
import CustomLink from '@components/atoms/custom-link/customLink';
import track from '@helper/tracking';
import StyledNavigation from './navigation.style';
import AngleDown from '../../icons/angleDown.svg';

const handleTracking = url => {
  track(['trackEvent', 'Navigation', 'Click', url]);
};

const SubNavigation = ({ navigation = [], openItemId, toggleItem = () => {} }) => {
  if (!navigation || navigation.length === 0) {
    return null;
  }

  return (
    <ul className="navigation__sub-grid-col">
      {navigation.map((item, index) => {
        if (item.children && item.children.length > 0) {
          const isItemOpen = openItemId === item.url;

          return (
            <li key={index}>
              <button
                className={`navigation__item-link ${isItemOpen ? 'is--open' : ''}`}
                onClick={() => toggleItem(item.url)}
                type="button"
              >
                <span>{item.title}</span>
                <AngleDown />
              </button>
              {isItemOpen && (
                <ul className="navigation__item-child">
                  {item.children.map(child => {
                    return (
                      <li key={child.title}>
                        <CustomLink
                          onClick={() => handleTracking(child.url)}
                          onKeyDown={e => (e.key === 'Enter' ? handleTracking(child.url) : '')}
                          className="navigation__item-child-link"
                          link={child}
                        >
                          {child.title}
                        </CustomLink>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        }
        return (
          <li key={item.title}>
            <CustomLink
              onClick={() => handleTracking(item.url)}
              onKeyDown={e => (e.key === 'Enter' ? handleTracking(item.url) : '')}
              className="navigation__item-link"
              link={item}
            >
              {item.title}
            </CustomLink>
          </li>
        );
      })}
    </ul>
  );
};

const Navigation = ({
  isOpen,
  navigation,
  subNavigationHeadline,
  subNavigationColumn1 = [],
  subNavigationColumn2 = [],
  loginMask
}) => {
  const [openItemId, setOpenItemId] = useState(null);

  const toggleItem = id => {
    if (openItemId === id) {
      setOpenItemId(null);
    } else {
      setOpenItemId(id);
    }
  };

  return (
    <StyledNavigation isOpen={isOpen} id="navigation">
      <div className="navigation__content-wrapper">
        <ul className="navigation__main-container">
          {navigation.length > 0 &&
            navigation.map(item => {
              if (item.children && item.children.length > 0) {
                const isItemOpen = openItemId === item.url;
                return (
                  <li key={item.title} className="navigation__main-item">
                    <button
                      className={`navigation__item-link ${isItemOpen ? 'is--open' : ''}`}
                      onClick={() => toggleItem(item.url)}
                      type="button"
                    >
                      <span>{item.title}</span>
                      <AngleDown width="24" height="24" />
                    </button>
                    {isItemOpen && (
                      <ul className="navigation__item-child">
                        {item.children.map(child => {
                          return (
                            <li key={child.title}>
                              <CustomLink
                                className="navigation__item-child-link"
                                link={child}
                                onClick={() => handleTracking(child.url)}
                              >
                                {child.title}
                              </CustomLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }
              return (
                <li key={item.title} className="navigation__main-item">
                  <CustomLink
                    link={item}
                    className="navigation__item-link"
                    onClick={() => handleTracking(item.url)}
                  >
                    {item.title}
                  </CustomLink>
                </li>
              );
            })}
        </ul>
        <div className="navigation__sub-container">
          {subNavigationHeadline && (
            <div className="navigation__sub-headline">
              <Copy>{subNavigationHeadline}</Copy>
            </div>
          )}
          <div className="navigation__sub-grid">
            <SubNavigation
              navigation={subNavigationColumn1}
              toggleItem={toggleItem}
              openItemId={openItemId}
            />
            <SubNavigation
              navigation={subNavigationColumn2}
              toggleItem={toggleItem}
              openItemId={openItemId}
            />
            {loginMask && (
              <div className="navigation__sub-token">
                {loginMask.headline && (
                  <Headline tag="h5" className="navigation__sub-token-headline">
                    {loginMask.headline}
                  </Headline>
                )}
                <CompactLoginMask {...loginMask} />
                {loginMask.copy && (
                  <MixedCopy
                    className="navigation__sub-token-copy"
                    html={loginMask.copy}
                    fullWidth
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledNavigation>
  );
};

SubNavigation.propTypes = {
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string
    })
  ),
  toggleItem: PropTypes.func,
  openItemId: PropTypes.string
};

Navigation.propTypes = {
  // autoLogout: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  loginMask: PropTypes.shape().isRequired,
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired,
  subNavigationHeadline: PropTypes.string,
  subNavigationColumn1: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string
    })
  ),
  subNavigationColumn2: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string
    })
  )
};

const mapStateToProps = state => ({
  // autoLogout: state.token.autoLogout,
  navigation: state.app.pageData.navigation,
  subNavigationHeadline: state.app.pageData.subNavigationHeadline,
  subNavigationColumn1: state.app.pageData.subNavigationColumn1,
  subNavigationColumn2: state.app.pageData.subNavigationColumn2,
  loginMask: state.app.pageData.loginMask,
  isOpen: state.navigation.isOpen
});

export { Navigation };
export default connect(mapStateToProps)(Navigation);
